import { Component, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FolioInvoiceApiModel, FolioInvoiceLineApiModel, ReservationApiModel } from '@rocksoft/inroom-sdk';
export interface FormInvoiceLine {
  invoiceLineCode: string;
  description: string;
  quantity: number;
  articleGroupCode: string;
  billingGroupCode: string;
  total: number;
  vatTotal: number;
  postingDate: string;
  customReference: string;
  isDeleted: boolean;
  currencyCode: string;
  revenueType: string;
}

export interface FormPaymentLine {
  invoiceLineCode: string;
  description: string;
  quantity: number;
  articleGroupCode: string;
  billingGroupCode: string;
  total: number;
  vatTotal: number;
  postingDate: string;
  customReference: string;
  currencyCode: string;
}

export interface FormInvoice {
  reservationId: string;
  total: number;
  vatTotal: number;
  invoiceReference: string;
  lines: FormInvoiceLine[];
  payments: FormPaymentLine[];
}

@Component({
  selector: 'era-mobilefolioforminvoice-ui',
  templateUrl: './mobilefolioforminvoice.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class MobileFolioFormInvoiceComponent {
  private readonly fb = inject(UntypedFormBuilder);

  public readonly group = input.required<UntypedFormGroup>();
  public readonly arrayName = input.required<string>();
  public readonly invoice = input.required<UntypedFormArray>();
  public readonly data = input.required<FolioInvoiceApiModel[]>();
  readonly editMode = input(false);
  readonly reservation = input.required<ReservationApiModel>();

  constructor() {
    effect(() => {
      const data = this.data();
      const reservation = this.reservation();
      if (!data || !this.invoice() || !reservation) return;

      data.forEach((value, index) => {
        this.addInvoice(reservation);

        value.Lines?.forEach(() => {
          this.addInvoiceLine(index);
        });
        value.Payments?.forEach(() => {
          this.addInvoicePaymentLine(index);
        });
      });

      const invoices: FormInvoice[] = data.map((x) => this.mapInvoiceToForm(x));
      this.invoice().patchValue(invoices);
    });
  }

  public invoiceLines(index: number): UntypedFormArray {
    return this.invoice().at(index).get('lines') as UntypedFormArray;
  }

  public invoicePaymentLines(index: number): UntypedFormArray {
    return this.invoice().at(index).get('payments') as UntypedFormArray;
  }

  public addInvoice(reservation: ReservationApiModel) {
    this.invoice().push(this.createInvoiceFormGroup(reservation));
  }

  public addInvoiceLine(index: number) {
    this.invoiceLines(index).push(this.createInvoiceLineFormGroup());
  }

  public addInvoicePaymentLine(index: number) {
    this.invoicePaymentLines(index).push(this.createPaymentLineFormGroup());
  }

  public deleteInvoice(index: number) {
    this.invoice().removeAt(index);
  }

  public deleteInvoiceLine(index: number, line: number) {
    this.invoiceLines(index).removeAt(line);
  }

  public deleteInvoicePaymentLine(index: number, line: number) {
    this.invoicePaymentLines(index).removeAt(line);
  }

  private createInvoiceFormGroup(reservation: ReservationApiModel): UntypedFormGroup {
    return this.fb.group({
      reservationId: [reservation?.Id ?? '', Validators.required],
      total: [10],
      vatTotal: [8],
      invoiceReference: ['ref'],
      lines: this.fb.array([]),
      payments: this.fb.array([]),
    });
  }

  private createInvoiceLineFormGroup(): UntypedFormGroup {
    return this.fb.group({
      invoiceLineCode: ['', Validators.required],
      description: ['desc'],
      quantity: [1],
      articleGroupCode: [''],
      billingGroupCode: [''],
      total: [2],
      vatTotal: [1],
      postingDate: [''],
      customReference: [''],
      isDeleted: [false],
      currencyCode: ['EUR'],
      revenueType: [''],
    });
  }

  private createPaymentLineFormGroup(): UntypedFormGroup {
    return this.fb.group({
      invoiceLineCode: ['', Validators.required],
      description: ['desc'],
      quantity: [1],
      articleGroupCode: [''],
      billingGroupCode: [''],
      total: [2],
      vatTotal: [1],
      postingDate: [''],
      customReference: [''],
      currencyCode: ['EUR'],
    });
  }

  private mapInvoiceLine(invoiceLine: FolioInvoiceLineApiModel): FormInvoiceLine {
    return {
      invoiceLineCode: invoiceLine.InvoiceLineCode ?? '',
      description: invoiceLine.Description ?? '',
      quantity: invoiceLine.Quantity ?? 0,
      articleGroupCode: invoiceLine.ArticleGroupCode ?? '',
      billingGroupCode: invoiceLine.BillingGroupCode ?? '',
      total: invoiceLine.Total ?? 0,
      vatTotal: invoiceLine.VatTotal ?? 0,
      postingDate: this.hackPostingDate(invoiceLine.PostingDate ?? ''),
      customReference: invoiceLine.CustomReference ?? '',
      isDeleted: invoiceLine.IsDeleted ?? false,
      currencyCode: invoiceLine.CurrencyCode ?? '',
      revenueType: invoiceLine.RevenueType ?? '',
    };
  }

  private mapPaymentLine(invoiceLine: FolioInvoiceLineApiModel): FormPaymentLine {
    return {
      invoiceLineCode: invoiceLine.InvoiceLineCode ?? '',
      description: invoiceLine.Description ?? '',
      quantity: invoiceLine.Quantity ?? 0,
      articleGroupCode: invoiceLine.ArticleGroupCode ?? '',
      billingGroupCode: invoiceLine.BillingGroupCode ?? '',
      total: invoiceLine.Total ?? 0,
      vatTotal: invoiceLine.VatTotal ?? 0,
      postingDate: this.hackPostingDate(invoiceLine.PostingDate ?? ''),
      customReference: invoiceLine.CustomReference ?? '',
      currencyCode: invoiceLine.CurrencyCode ?? '',
    };
  }

  private mapInvoiceToForm(invoice: FolioInvoiceApiModel): FormInvoice {
    return {
      reservationId: invoice.ReservationId?.toString() ?? '',
      total: invoice.Total ?? 0,
      vatTotal: invoice.VatTotal ?? 0,
      invoiceReference: invoice.InvoiceReference ?? '',
      lines: invoice.Lines?.map((x) => this.mapInvoiceLine(x)) ?? [],
      payments: invoice.Payments?.map((x) => this.mapPaymentLine(x)) ?? [],
    };
  }

  // HACK: the InRoom SDK is lying about it's type; it returns a string
  // but the SDK tells us the type is a Date. So we have to parse it.
  private hackPostingDate(date: string | Date | undefined): string {
    // Guard
    if (typeof date === 'undefined') {
      return new Date().toISOString().substring(0, 10);
    }

    if (typeof date === 'string') {
      return date.substring(0, 10);
    }

    return date.toISOString().substring(0, 10);
  }
}
